import type { AccountListResponse } from '@/api/accounts'
import { useCountUp } from '@/hooks/useCountUp'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'

const count = (n: number) => (n === 1 ? '1 account' : `${n} accounts`)

/** Household-wide figures — filters never change them. Mounted only once data is in, so the count-up runs once. */
export function AccountsHero({
  data,
  haveCount,
  oweCount,
  readyToSpend,
}: {
  data: AccountListResponse
  haveCount: number
  oweCount: number
  /** Null while earmarks are loading. */
  readyToSpend: number | null
}) {
  const { format: exact, formatWhole } = useFormatCurrency()
  const wide = useMediaQuery(DESKTOP_QUERY)
  // A phone hero is a glance, like the Dashboard: whole rupees.
  const format = wide ? exact : formatWhole
  const netWorth = useCountUp(data.netWorth)
  // An older or partial response can omit the side totals.
  const have = data.totalAssets ?? 0
  const owe = data.totalLiabilities ?? 0

  return (
    <section
      aria-label="Net worth"
      className="grid gap-2 rounded-2xl border border-[var(--fl-hero-border)] bg-gradient-to-br from-[var(--fl-hero-bg)] to-[var(--fl-hero-bg-2)] px-3.5 py-3 text-[var(--fl-hero-fg)] motion-safe:animate-fade-slide-up md:gap-4 md:rounded-[20px] md:p-6 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:items-end lg:gap-7"
    >
      <div>
        <div className="flex items-baseline justify-between gap-3 md:block">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-[13px] md:font-normal md:normal-case md:tracking-normal">
            Net worth
          </p>
          <p
            data-testid="net-worth"
            className="font-serif text-[22px] leading-tight font-bold tracking-tight tabular-nums md:mt-1 md:text-[42px] md:font-semibold"
          >
            {format(netWorth)}
          </p>
        </div>
        {(have > 0 || owe > 0) && (
          <div
            aria-hidden="true"
            className="mt-2 flex h-1.5 gap-0.5 overflow-hidden rounded md:mt-3 md:h-2"
          >
            {have > 0 && <div className="bg-[var(--fl-hero-bar)]" style={{ flex: have }} />}
            {owe > 0 && (
              <div className="bg-[var(--fl-hero-expense)] opacity-70" style={{ flex: owe }} />
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between gap-3 md:grid md:grid-cols-2 lg:contents">
        <div className="flex items-baseline gap-1.5 md:block">
          <p className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
            You have{wide && ` · ${count(haveCount)}`}
          </p>
          <p
            data-testid="total-have"
            className="text-[13px] font-semibold tabular-nums text-[var(--fl-hero-income)] md:mt-0.5 md:font-serif md:text-2xl"
          >
            {format(have)}
          </p>
        </div>
        <div className="flex items-baseline gap-1.5 md:block">
          <p className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
            You owe{wide && ` · ${count(oweCount)}`}
          </p>
          <p
            data-testid="total-owe"
            className="text-[13px] font-semibold tabular-nums text-[var(--fl-hero-expense)] md:mt-0.5 md:font-serif md:text-2xl"
          >
            {format(owe)}
          </p>
        </div>
      </div>
      {readyToSpend !== null && (
        <div className="flex items-baseline justify-between gap-3 border-t border-[var(--fl-hero-bar-faint)] pt-2 md:pt-3 lg:block lg:border-0 lg:pt-0">
          <div className="min-w-0">
            <p className="truncate text-[13px] text-[var(--fl-hero-fg-2)] md:whitespace-normal">
              Ready to spend
              {!wide && <span className="text-[11px]"> · bank, cash &amp; wallets − goals</span>}
            </p>
            {wide && (
              <p className="text-xs text-[var(--fl-hero-fg-2)]">
                Bank, cash &amp; wallets, minus goal money
              </p>
            )}
          </div>
          <p
            data-testid="ready-to-spend"
            className="shrink-0 text-[15px] font-semibold tabular-nums md:font-serif md:text-2xl lg:mt-0.5"
          >
            {format(readyToSpend)}
          </p>
        </div>
      )}
    </section>
  )
}
